// src/components/Gallery/Gallery.tsx
import { useCallback, useEffect, useRef } from "react";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { Modal } from "@appcues/sonar";
import { Icon as Icon2 } from "@appcues/sonar-builder";

// src/context/Context.tsx
import { createContext, useContext } from "react";
var Context = createContext(void 0);
var useImagery = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useImagery must be used within an ImageryProvider");
  }
  return context;
};
var Context_default = Context;

// src/components/Gallery/Card.tsx
import styled from "styled-components";
import { faImage } from "@fortawesome/pro-solid-svg-icons/faImage";
import { Row, Text, Stack } from "@appcues/sonar";
import { Icon } from "@appcues/sonar-builder";

// src/components/Gallery/gallery-helpers.ts
var formatDate = (date, locale = "en-US") => new Intl.DateTimeFormat(locale, {
  year: "numeric",
  month: "short",
  day: "numeric"
}).format(new Date(date));
var formatSize = (size) => `${(size / 1e3).toFixed(2)} KB`;

// src/components/Gallery/Card.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var InvalidMessage = styled.div`
  position: absolute;
  display: none;
  min-width: 210px;
  text-align: center;
  color: var(--foreground-primary);
  background-color: var(--background-default-base);
  padding: var(--spacing-small);
  border: 1px solid var(--border-input-default);
  border-radius: var(--border-radius-small);
`;
var Root = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 220px;
  min-height: auto;
  background: var(--background-level-1);
  border: 1px solid var(--border-input-default);
  border-radius: var(--border-radius-small);
  overflow: hidden;
  transition:
    opacity 0.2s,
    outline 0.2s;

  cursor: ${({ isDisabled }) => isDisabled ? "not-allowed" : "pointer"};
  opacity: ${({ isDisabled }) => isDisabled ? 0.5 : 1};

  &:hover {
    outline: ${({ isDisabled }) => isDisabled ? "none" : "1px solid var(--border-input-hover)"};

    ${InvalidMessage} {
      display: ${({ isDisabled }) => isDisabled ? "block" : "none"};
    }
  }
`;
var ImageWrapper = styled.div`
  position: relative;
  display: flex;
  height: 150px;
  align-items: center;
  justify-content: center;
  background-color: var(--color-neutral-100);
`;
var ImageSource = styled.img`
  display: flex;
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  object-fit: contain;
  object-position: center;
`;
var Title = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
`;
var Footer = styled.footer`
  background-color: var(--color-neutral-200);
  padding: var(--spacing-small);
`;
var Card = ({ image, onClick, invalidMessage }) => {
  const { id, name, url, width, height, size, format, updatedAt } = image;
  const isDisabled = Boolean(invalidMessage);
  return /* @__PURE__ */ jsxs(
    Root,
    {
      onClick: () => isDisabled ? null : onClick(image),
      isDisabled,
      children: [
        /* @__PURE__ */ jsxs(ImageWrapper, { children: [
          /* @__PURE__ */ jsx(ImageSource, { src: url }),
          invalidMessage && /* @__PURE__ */ jsx(InvalidMessage, { children: invalidMessage })
        ] }),
        /* @__PURE__ */ jsx(Footer, { children: /* @__PURE__ */ jsxs(Stack, { spacing: "x-small", children: [
          /* @__PURE__ */ jsxs(Row, { fluid: true, xAlign: "space-between", children: [
            /* @__PURE__ */ jsx(Title, { weight: "bold", title: name, children: name }),
            /* @__PURE__ */ jsx(Text, { weight: "bold", size: "small", children: formatDate(updatedAt) })
          ] }),
          /* @__PURE__ */ jsxs(Row, { fluid: true, xAlign: "space-between", children: [
            /* @__PURE__ */ jsxs(Row, { yAlign: "center", spacing: "x-small", children: [
              /* @__PURE__ */ jsx(Icon, { size: "x-small", icon: faImage }),
              /* @__PURE__ */ jsxs(Text, { size: "small", children: [
                width,
                " x ",
                height,
                " / ",
                format.toUpperCase()
              ] })
            ] }),
            /* @__PURE__ */ jsx(Text, { size: "small", children: formatSize(size) })
          ] })
        ] }) })
      ]
    },
    id
  );
};

// src/components/Gallery/EmptyState.tsx
import styled2 from "styled-components";
import { Spinner, Stack as Stack2, SubTitle, Text as Text2 } from "@appcues/sonar";

// src/components/Gallery/EmptyIcon.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var EmptyIcon = () => {
  return /* @__PURE__ */ jsxs2(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      width: "160",
      height: "160",
      children: [
        /* @__PURE__ */ jsxs2("g", { clipPath: "url(#a)", children: [
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "color(display-p3 .9569 .9843 1)",
              d: "M80.722 160.838c44.182 0 80-35.817 80-80 0-44.182-35.818-80-80-80-44.183 0-80 35.818-80 80 0 44.183 35.817 80 80 80Z"
            }
          ),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "#fff",
              stroke: "color(display-p3 .3608 .3608 1)",
              strokeLinecap: "round",
              strokeLinejoin: "round",
              d: "M19.105 94.555a.14.14 0 0 1 .106-.094l1.874-.512a.14.14 0 0 0 .105-.135.144.144 0 0 0-.105-.14l-1.874-.516a.152.152 0 0 1-.101-.102l-.517-1.875a.14.14 0 0 0-.135-.106.144.144 0 0 0-.14.106l-.511 1.875a.147.147 0 0 1-.097.102l-1.883.512a.135.135 0 0 0-.105.135.14.14 0 0 0 .106.136l1.882.52a.153.153 0 0 1 .101.102l.508 1.875a.148.148 0 0 0 .14.106.14.14 0 0 0 .134-.106l.512-1.883Z"
            }
          ),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "color(display-p3 .9137 .9686 1)",
              d: "M15.722 127.838h130v40.405h-130z"
            }
          ),
          /* @__PURE__ */ jsx2(
            "path",
            {
              stroke: "color(display-p3 .3608 .3608 1)",
              strokeLinecap: "round",
              strokeWidth: ".878",
              d: "M24.945 128.278h111.554m4.391 0h4.392m-129.121 0h4.392"
            }
          )
        ] }),
        /* @__PURE__ */ jsx2(
          "path",
          {
            fill: "#fff",
            stroke: "color(display-p3 .3608 .3608 1)",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M130.215 42.568a.413.413 0 0 1 .31-.273l5.482-1.498a.407.407 0 0 0 .309-.397.422.422 0 0 0-.309-.409l-5.482-1.51a.448.448 0 0 1-.297-.297l-1.51-5.486a.407.407 0 0 0-.396-.31.424.424 0 0 0-.408.31l-1.497 5.486a.437.437 0 0 1-.285.297l-5.507 1.498a.395.395 0 0 0-.309.397.41.41 0 0 0 .309.396l5.507 1.523a.448.448 0 0 1 .297.297l1.485 5.486a.433.433 0 0 0 .408.31.406.406 0 0 0 .396-.31l1.497-5.51Z"
          }
        ),
        /* @__PURE__ */ jsx2(
          "path",
          {
            fill: "url(#b)",
            stroke: "color(display-p3 .3608 .3608 1)",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M69.959 17.271a.519.519 0 0 1 .393-.346l6.966-1.904a.519.519 0 0 0 .393-.504.535.535 0 0 0-.393-.52l-6.966-1.92a.567.567 0 0 1-.378-.377l-1.918-6.972a.519.519 0 0 0-.504-.394.534.534 0 0 0-.519.394L65.131 11.7a.551.551 0 0 1-.362.378l-6.998 1.904a.504.504 0 0 0-.393.504.52.52 0 0 0 .393.503l6.998 1.936a.566.566 0 0 1 .377.378l1.887 6.972a.551.551 0 0 0 .52.394.519.519 0 0 0 .503-.394l1.903-7.004Z"
          }
        ),
        /* @__PURE__ */ jsx2(
          "path",
          {
            fill: "#fff",
            stroke: "color(display-p3 .3608 .3608 1)",
            d: "m58.399 111.856-.003.001a1.416 1.416 0 0 1-1.736-.996v-.002L42.394 56.753l-.002-.007a1.4 1.4 0 0 1 .995-1.731L95.28 41.383a1.417 1.417 0 0 1 1.724 1.01l12.224 46.22c1.342 5.075-1.686 10.275-6.761 11.615l-44.068 11.628Z"
          }
        ),
        /* @__PURE__ */ jsx2(
          "path",
          {
            fill: "#fff",
            stroke: "color(display-p3 .3608 .3608 1)",
            strokeLinejoin: "round",
            d: "M102.07 86.577 61.676 97.242a1.933 1.933 0 0 1-2.4-1.35l-8.999-34.095a1.916 1.916 0 0 1 1.367-2.333l38.595-10.232a1.933 1.933 0 0 1 2.35 1.366l9.481 35.979Z"
          }
        ),
        /* @__PURE__ */ jsx2(
          "path",
          {
            fill: "#fff",
            stroke: "color(display-p3 .3608 .3608 1)",
            d: "M63.776 61.547h54.159c.644 0 1.167.522 1.167 1.166v56.459c0 .644-.523 1.167-1.167 1.167h-54.16a1.167 1.167 0 0 1-1.166-1.167V62.713c0-.644.523-1.166 1.167-1.166Z"
          }
        ),
        /* @__PURE__ */ jsx2(
          "path",
          {
            fill: "color(display-p3 .8431 .8431 1)",
            stroke: "color(display-p3 .3608 .3608 1)",
            d: "M110.972 67.713H71.176a2.14 2.14 0 0 0-2.143 2.138V105a2.14 2.14 0 0 0 2.143 2.138h39.796a2.14 2.14 0 0 0 2.143-2.138V69.851a2.14 2.14 0 0 0-2.143-2.138Z"
          }
        ),
        /* @__PURE__ */ jsx2(
          "mask",
          {
            id: "c",
            width: "44",
            height: "39",
            x: "69",
            y: "68",
            maskUnits: "userSpaceOnUse",
            style: { maskType: "alpha" },
            children: /* @__PURE__ */ jsx2(
              "path",
              {
                fill: "color(display-p3 .8431 .8431 1)",
                stroke: "color(display-p3 .3608 .3608 1)",
                d: "M71.176 68.713h39.796a1.14 1.14 0 0 1 1.143 1.138V105a1.14 1.14 0 0 1-1.143 1.138H71.176A1.14 1.14 0 0 1 70.033 105V69.851c0-.627.51-1.138 1.143-1.138Z"
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs2("g", { mask: "url(#c)", children: [
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "#fff",
              stroke: "color(display-p3 .3608 .3608 1)",
              strokeLinejoin: "round",
              d: "M119.168 107.22s-9.782-8.959-11.299-16.25a2.233 2.233 0 0 0-.796-1.294 2.31 2.31 0 0 0-1.453-.504h-9.666a2.311 2.311 0 0 0-1.488.512c-.418.338-.7.812-.795 1.335-1.383 7.275-10.165 16.201-10.165 16.201h35.662Z"
            }
          ),
          /* @__PURE__ */ jsx2("mask", { id: "d", fill: "#fff", children: /* @__PURE__ */ jsx2(
            "path",
            {
              fillRule: "evenodd",
              d: "M67.173 83.35c-1.196 0-2.297-.8-2.297-1.996a7.319 7.319 0 0 1 13.904-3.197.567.567 0 0 0 .512.314c1.284 0 2.434.574 3.207 1.48.608.713 1.622 1.181 2.56 1.181 2.052 0 1.223 2.218-.829 2.218H67.173Z",
              clipRule: "evenodd"
            }
          ) }),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "#fff",
              fillRule: "evenodd",
              d: "M67.173 83.35c-1.196 0-2.297-.8-2.297-1.996a7.319 7.319 0 0 1 13.904-3.197.567.567 0 0 0 .512.314c1.284 0 2.434.574 3.207 1.48.608.713 1.622 1.181 2.56 1.181 2.052 0 1.223 2.218-.829 2.218H67.173Z",
              clipRule: "evenodd"
            }
          ),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "color(display-p3 .3608 .3608 1)",
              d: "m78.78 78.157-.899.437.9-.437Zm3.72 1.793-.761.65.76-.65Zm-16.623 1.404a6.32 6.32 0 0 1 6.319-6.32v-2a8.319 8.319 0 0 0-8.319 8.32h2Zm6.319-6.32a6.32 6.32 0 0 1 5.686 3.56l1.799-.875a8.32 8.32 0 0 0-7.485-4.684v2Zm7.097 4.437c.979 0 1.855.436 2.446 1.129l1.521-1.299a5.205 5.205 0 0 0-3.967-1.83v2Zm4.938 2.879H67.173v2H84.23v-2Zm.829-.218c.36 0 .353.1.266-.001-.114-.133-.07-.262-.079-.24-.012.034-.078.15-.26.263a1.422 1.422 0 0 1-.756.196v2c1.36 0 2.51-.74 2.89-1.76.199-.531.193-1.215-.278-1.763-.445-.517-1.119-.695-1.783-.695v2Zm-7.178-3.538c.27.556.832.877 1.41.877v-2c.148 0 .307.081.389.248l-1.799.875Zm3.857 2.006c.798.935 2.09 1.532 3.32 1.532v-2c-.644 0-1.38-.34-1.799-.83L81.738 80.6Zm-17.862.754c0 1.921 1.74 2.996 3.297 2.996v-2c-.835 0-1.297-.526-1.297-.996h-2Z",
              mask: "url(#d)"
            }
          ),
          /* @__PURE__ */ jsx2("mask", { id: "e", fill: "#fff", children: /* @__PURE__ */ jsx2(
            "path",
            {
              fillRule: "evenodd",
              d: "M77.652 79.857c-1.196 0-2.297-.8-2.297-1.996a7.319 7.319 0 0 1 13.904-3.197.567.567 0 0 0 .512.313c1.284 0 2.433.575 3.206 1.48.61.714 1.623 1.182 2.56 1.182 2.052 0 1.224 2.218-.828 2.218H77.652Z",
              clipRule: "evenodd"
            }
          ) }),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "#fff",
              fillRule: "evenodd",
              d: "M77.652 79.857c-1.196 0-2.297-.8-2.297-1.996a7.319 7.319 0 0 1 13.904-3.197.567.567 0 0 0 .512.313c1.284 0 2.433.575 3.206 1.48.61.714 1.623 1.182 2.56 1.182 2.052 0 1.224 2.218-.828 2.218H77.652Z",
              clipRule: "evenodd"
            }
          ),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "color(display-p3 .3608 .3608 1)",
              d: "m89.26 74.664.899-.438-.9.438Zm3.717 1.793-.76.65.76-.65Zm-16.622 1.404a6.32 6.32 0 0 1 6.319-6.32v-2a8.319 8.319 0 0 0-8.319 8.32h2Zm6.319-6.32a6.32 6.32 0 0 1 5.686 3.56l1.799-.875a8.32 8.32 0 0 0-7.485-4.684v2Zm7.097 4.436c.979 0 1.855.437 2.446 1.13l1.521-1.299a5.205 5.205 0 0 0-3.967-1.83v2Zm4.938 2.88H77.652v2h17.057v-2Zm.828-.218c.362 0 .353.099.267-.002-.114-.132-.07-.262-.079-.24-.012.035-.079.15-.26.264a1.423 1.423 0 0 1-.756.196v2c1.36 0 2.51-.74 2.89-1.76.199-.531.193-1.215-.279-1.764-.444-.516-1.118-.694-1.782-.694v2ZM88.36 75.1c.27.555.832.876 1.41.876v-2c.148 0 .307.082.389.25l-1.799.874Zm3.857 2.005c.798.936 2.09 1.533 3.32 1.533v-2c-.644 0-1.38-.34-1.799-.831l-1.521 1.298Zm-17.862.755c0 1.921 1.74 2.996 3.297 2.996v-2c-.835 0-1.297-.526-1.297-.996h-2Z",
              mask: "url(#e)"
            }
          ),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "#fff",
              stroke: "color(display-p3 .3608 .3608 1)",
              d: "M108.622 76.364a4.157 4.157 0 1 1-8.314 0 4.157 4.157 0 0 1 8.314 0Z"
            }
          ),
          /* @__PURE__ */ jsx2(
            "path",
            {
              fill: "#fff",
              stroke: "color(display-p3 .3608 .3608 1)",
              strokeLinejoin: "round",
              d: "M107.376 90.918c0 .965-2.998 1.747-6.695 1.747-3.698 0-6.696-.782-6.696-1.747 0-1.746 2.998-1.746 6.696-1.746 3.697 0 6.695 0 6.695 1.746Z"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs2("defs", { children: [
          /* @__PURE__ */ jsxs2(
            "linearGradient",
            {
              id: "b",
              x1: "57.377",
              x2: "77.711",
              y1: "14.502",
              y2: "14.502",
              gradientUnits: "userSpaceOnUse",
              children: [
                /* @__PURE__ */ jsx2("stop", { stopColor: "color(display-p3 .3608 .3608 1)" }),
                /* @__PURE__ */ jsx2("stop", { offset: "1", stopColor: "color(display-p3 .5373 .3765 1)" })
              ]
            }
          ),
          /* @__PURE__ */ jsx2("clipPath", { id: "a", children: /* @__PURE__ */ jsx2(
            "rect",
            {
              width: "160",
              height: "160",
              x: ".722",
              y: ".838",
              fill: "#fff",
              rx: "80"
            }
          ) })
        ] })
      ]
    }
  );
};

// src/components/Gallery/EmptyState.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var Wrapper = styled2(Stack2)`
  text-align: center;
  margin: var(--spacing-regular) auto;
`;
var EmptyState = ({ isLoading }) => {
  return /* @__PURE__ */ jsx3(Wrapper, { xAlign: "center", yAlign: "center", spacing: "regular", children: isLoading ? /* @__PURE__ */ jsx3(Spinner, {}) : /* @__PURE__ */ jsxs3(Fragment, { children: [
    /* @__PURE__ */ jsx3(EmptyIcon, {}),
    /* @__PURE__ */ jsx3(SubTitle, { children: "Empty Gallery" }),
    /* @__PURE__ */ jsxs3(Text2, { children: [
      "Once you upload images to any Appcues experience, ",
      /* @__PURE__ */ jsx3("br", {}),
      "you will see them appear here."
    ] })
  ] }) });
};

// src/components/Gallery/Grid.tsx
import styled3 from "styled-components";
import { jsx as jsx4 } from "react/jsx-runtime";
var StyledGrid = styled3.div`
  display: grid;
  grid-template-columns: ${({ $hasItems }) => $hasItems ? `repeat(3, 1fr)` : `1fr`};
  gap: var(--spacing-regular);
  max-height: 500px;
  min-height: 235px;
  overflow-y: auto;
  padding: var(--spacing-small);
  border: 1px solid var(--border-default);
  border-radius: var(--border-radius-small);
`;
var Grid = ({ children, hasItems }) => /* @__PURE__ */ jsx4(StyledGrid, { $hasItems: hasItems, children });

// src/components/validators.ts
var getFileInfo = (file) => {
  if ("type" in file) {
    return {
      type: file.type?.toLowerCase(),
      extension: `.${file?.name?.toLowerCase().split(".").pop()}`
    };
  }
  return {
    extension: file.format
  };
};
var validateFormat = (file, accept) => {
  if (!file || !accept) {
    return false;
  }
  const acceptedTypes = accept.split(",").map((format) => format.trim().toLowerCase());
  const { type, extension } = getFileInfo(file);
  const isValid = acceptedTypes.some((acceptedType) => {
    if (type === acceptedType) {
      return true;
    }
    if (type?.startsWith("image/") && acceptedType === "image/*") {
      return true;
    }
    if (acceptedType.includes("*")) {
      const [acceptedCategory] = acceptedType.split("/");
      const [fileCategory] = type?.split("/") || [];
      if (acceptedCategory === fileCategory) {
        return true;
      }
      if (!type && extension && acceptedCategory === "image") {
        return true;
      }
      return false;
    }
    const acceptedExtension = acceptedType.split("/")[1];
    if (acceptedExtension === extension) {
      return true;
    }
    return false;
  });
  return isValid;
};
var validateDimension = (image, maxDimension) => {
  return !maxDimension || image.width <= maxDimension && image.height <= maxDimension;
};
var validateSize = (file, maxSize) => {
  return !maxSize || file.size <= maxSize * 1024 * 1024;
};

// src/components/Gallery/image-validator.ts
var validateImage = ({
  image,
  accept,
  maxSize,
  maxDimension
}) => {
  const validations = [
    {
      isValid: validateFormat(image, accept),
      message: "format"
    },
    {
      isValid: validateSize(image, maxSize),
      message: "size"
    },
    {
      isValid: validateDimension(image, maxDimension),
      message: "dimension"
    }
  ];
  const invalidMessages = validations.filter((validation) => !validation.isValid).map((validation) => validation.message);
  const isValid = invalidMessages.length === 0;
  const tooltipContent = !isValid ? `Invalid ${invalidMessages.join(", ").replace(/, ([^,]*)$/, " and $1")}` : "";
  return { status: isValid, invalidMessage: tooltipContent };
};

// src/components/Gallery/Gallery.tsx
import { Fragment as Fragment2, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var Gallery = ({
  accept = "image/*",
  maxSize = 1,
  maxDimension,
  container,
  onSelect
}) => {
  const sentinelRef = useRef(null);
  const {
    images,
    isLoading,
    isGalleryOpen,
    setIsGalleryOpen,
    pageInfo,
    fetchImages
  } = useImagery();
  const { number, size, total } = pageInfo;
  const hasItems = total > 0;
  useEffect(() => {
    if (isGalleryOpen) {
      fetchImages(1);
    }
  }, [isGalleryOpen, fetchImages]);
  const fetchNextPage = useCallback(() => {
    if (number * size < total) {
      const nextPage = number + 1;
      fetchImages(nextPage);
    }
  }, [number, size, total, fetchImages]);
  useEffect(() => {
    const currentSentinel = sentinelRef.current;
    if (!currentSentinel) return;
    const observerInstance = new IntersectionObserver(
      (entries) => {
        const [{ isIntersecting }] = entries;
        if (isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observerInstance.observe(currentSentinel);
    return () => {
      if (currentSentinel) {
        observerInstance.unobserve(currentSentinel);
      }
      observerInstance.disconnect();
    };
  }, [fetchNextPage]);
  const handleClick = (image) => {
    onSelect?.(image);
    setIsGalleryOpen(false);
  };
  return /* @__PURE__ */ jsxs4(
    Modal.Root,
    {
      open: isGalleryOpen,
      onOpenChange: () => setIsGalleryOpen(false),
      container,
      size: "large",
      children: [
        /* @__PURE__ */ jsx5(Modal.Title, { children: "Image Gallery" }),
        /* @__PURE__ */ jsx5(Grid, { hasItems, children: hasItems ? /* @__PURE__ */ jsxs4(Fragment2, { children: [
          images.map((image) => {
            const { invalidMessage } = validateImage({
              image,
              accept,
              maxSize,
              maxDimension
            });
            return /* @__PURE__ */ jsx5(
              Card,
              {
                image,
                onClick: handleClick,
                invalidMessage
              },
              image.id
            );
          }),
          /* @__PURE__ */ jsx5(
            "div",
            {
              ref: sentinelRef,
              className: "col-span-full h-4",
              "aria-hidden": "true"
            }
          )
        ] }) : /* @__PURE__ */ jsx5(EmptyState, { isLoading }) }),
        /* @__PURE__ */ jsx5(Modal.Close, { "aria-label": "Close", children: /* @__PURE__ */ jsx5(Icon2, { size: "large", icon: faXmark }) })
      ]
    }
  );
};

// src/components/Uploader/Uploader.tsx
import {
  useEffect as useEffect3,
  useMemo,
  useReducer,
  useRef as useRef2,
  useState as useState2
} from "react";
import { faCirclePlus } from "@fortawesome/pro-regular-svg-icons/faCirclePlus";
import { faUpload } from "@fortawesome/pro-regular-svg-icons/faUpload";
import { faImages } from "@fortawesome/pro-regular-svg-icons/faImages";
import { DropdownMenu, Row as Row5, Stack as Stack6, Text as Text6 } from "@appcues/sonar";
import { Icon as Icon7 } from "@appcues/sonar-builder";

// src/components/Uploader/SelectedImage.tsx
import { faTrashXmark } from "@fortawesome/pro-solid-svg-icons/faTrashXmark";
import { Button, Row as Row2, Text as Text3 } from "@appcues/sonar";
import { Icon as Icon3 } from "@appcues/sonar-builder";
import styled4 from "styled-components";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var TitleText = styled4(Text3)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  text-align: left;
`;
var SelectedImage = ({ image, handleClean }) => {
  return /* @__PURE__ */ jsxs5(Row2, { spacing: "small", yAlign: "center", xAlign: "space-between", children: [
    /* @__PURE__ */ jsx6(TitleText, { weight: "bold", colorToken: "foreground-primary", children: image }),
    /* @__PURE__ */ jsx6(
      Button,
      {
        variant: "tertiary",
        iconOnly: true,
        onClick: handleClean,
        "aria-label": "remove file",
        children: /* @__PURE__ */ jsx6(
          Icon3,
          {
            icon: faTrashXmark,
            colorToken: "background-button-destructive-default"
          }
        )
      }
    )
  ] });
};

// src/components/Uploader/LoadingIcon.tsx
import styled5 from "styled-components";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { Icon as Icon4 } from "@appcues/sonar-builder";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var StyledLoadingIcon = styled5.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  svg:first-child {
    animation: rotate 1s linear infinite;
    transform-origin: center;
  }

  svg:last-child {
    position: absolute;
  }
`;
var LoadingIcon = () => /* @__PURE__ */ jsxs6(StyledLoadingIcon, { children: [
  /* @__PURE__ */ jsxs6(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      children: [
        /* @__PURE__ */ jsx7(
          "path",
          {
            d: "M24 12C24 14.5342 23.1977 17.0033 21.7082 19.0534C20.2187 21.1036 18.1183 22.6296 15.7082 23.4127C13.2981 24.1958 10.7019 24.1958 8.2918 23.4127C5.88167 22.6296 3.78133 21.1036 2.2918 19.0534C0.802259 17.0033 -2.21542e-07 14.5342 0 12C2.21543e-07 9.46585 0.80226 6.99675 2.2918 4.94658C3.78133 2.8964 5.88168 1.37042 8.2918 0.587321C10.7019 -0.195774 13.2981 -0.195774 15.7082 0.587322L15.1149 2.41335C13.0904 1.75555 10.9096 1.75555 8.88511 2.41335C6.86061 3.07115 5.09632 4.35298 3.84511 6.07512C2.5939 7.79727 1.92 9.87131 1.92 12C1.92 14.1287 2.5939 16.2027 3.84511 17.9249C5.09632 19.647 6.86061 20.9288 8.88511 21.5866C10.9096 22.2444 13.0904 22.2445 15.1149 21.5866C17.1394 20.9288 18.9037 19.647 20.1549 17.9249C21.4061 16.2027 22.08 14.1287 22.08 12H24Z",
            fill: "#DCE4F2",
            id: "circlePath"
          }
        ),
        /* @__PURE__ */ jsx7(
          "path",
          {
            id: "movingPath",
            d: "M12 0C13.5759 1.8792e-08 15.1363 0.310389 16.5922 0.913446C18.0481 1.5165 19.371 2.40042 20.4853 3.51472C21.5996 4.62902 22.4835 5.95189 23.0866 7.4078C23.6896 8.86371 24 10.4241 24 12L22.08 12C22.08 10.6763 21.8193 9.36551 21.3127 8.14255C20.8061 6.91959 20.0637 5.80838 19.1276 4.87236C18.1916 3.93635 17.0804 3.19386 15.8574 2.68729C14.6345 2.18073 13.3237 1.92 12 1.92L12 0Z",
            fill: "#0056A1"
          }
        )
      ]
    }
  ),
  /* @__PURE__ */ jsx7(Icon4, { icon: faArrowUp, colorToken: "foreground-primary", size: "x-small" })
] });

// src/components/Uploader/ErrorMessage.tsx
import { faHexagonExclamation } from "@fortawesome/pro-solid-svg-icons/faHexagonExclamation";
import { Row as Row3, Stack as Stack4, Text as Text4 } from "@appcues/sonar";
import { Icon as Icon5 } from "@appcues/sonar-builder";

// src/components/Uploader/styled.ts
import styled6 from "styled-components";
import { Button as Button2, Stack as Stack3 } from "@appcues/sonar";
var UploaderBox = styled6.button`
  width: 100%;
  min-height: 94px;
  padding: var(--spacing-regular);
  border-radius: var(--border-radius-small);
  font-family: 'Lato', var(--font-family-body);

  // Focus in this case changes background and border color no need for outline
  outline: none;

  background: var(--background-neutral-1);
  border: 1px dashed var(--border-neutral-1);

  &&[data-state='open'] {
    background: var(--background-brand-base);
    border: 1px dashed var(--border-neutral-1);
  }

  // Override font-family for all child elements
  * {
    font-family: 'Lato', var(--font-family-body);
  }

  ${(props) => props.status !== "showing_image" && `
      cursor: pointer;

      &:hover,
      &:focus {
        background: var(--background-brand-base);
      }
    `}

  ${(props) => props.status === "warning" && `
    background: var(--background-warning);
    border: 1px dashed var(--border-warning);

    &:hover,
    &:focus {
      background: var(--background-warning);
    }
  `}

  ${(props) => props.status === "error" && `
    background: var(--background-error-base);
    border: 1px dashed var(--border-error);

    &:hover,
    &:focus {
      background: var(--background-error-base);
    }
  `}

  ${(props) => props.status === "drop" && `
    background: var(--background-highlight-3, #e3eafa);
    border: 1px dashed var(--border-highlight-2, #5255bc);
  `}

  > input {
    position: absolute;
    height: 0;
    width: 0;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
  }
`;
var BackButton = styled6(Button2)`
  && {
    padding: 2px var(--spacing-regular);

    &:hover:not(:active):not([disabled]),
    &:focus:not(:active):not([disabled]) {
      background: transparent;
    }
  }
`;
var DropContent = styled6(Stack3)`
  svg,
  span,
  p {
    color: var(--color-blue2-600, var(--foreground-highlight, #155dfc));
  }
`;
var StyledDropdownContent = styled6.div`
  /* Higher specificity than Radix default styles */
  && {
    ${(props) => props.$zIndex && `z-index: ${props.$zIndex};`}
  }
`;

// src/components/Uploader/ErrorMessage.tsx
import { jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
var ErrorMessage = ({ message, onTryAgain }) => {
  return /* @__PURE__ */ jsx8(UploaderBox, { status: "error", as: "div", children: /* @__PURE__ */ jsxs7(Stack4, { spacing: "small", xAlign: "center", children: [
    /* @__PURE__ */ jsxs7(Row3, { spacing: "small", yAlign: "center", children: [
      /* @__PURE__ */ jsx8(Icon5, { icon: faHexagonExclamation, colorToken: "foreground-error" }),
      /* @__PURE__ */ jsx8(Text4, { weight: "bold", colorToken: "foreground-error", children: message })
    ] }),
    /* @__PURE__ */ jsx8(BackButton, { variant: "tertiary", onClick: onTryAgain, children: "Try again" })
  ] }) });
};

// src/components/Uploader/WarningMessage.tsx
import { useEffect as useEffect2, useState } from "react";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { Row as Row4, Stack as Stack5, Text as Text5 } from "@appcues/sonar";
import { Icon as Icon6 } from "@appcues/sonar-builder";
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var WarningMessage = ({
  message,
  onReset,
  resetDelay = 5
}) => {
  const [countdown, setCountdown] = useState(resetDelay);
  useEffect2(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1e3);
    return () => clearInterval(timer);
  }, [onReset]);
  return /* @__PURE__ */ jsx9(UploaderBox, { status: "warning", as: "div", children: /* @__PURE__ */ jsxs8(Stack5, { spacing: "small", xAlign: "center", children: [
    /* @__PURE__ */ jsxs8(Row4, { spacing: "small", yAlign: "center", children: [
      /* @__PURE__ */ jsx9(Icon6, { icon: faTriangleExclamation, colorToken: "foreground-warning" }),
      /* @__PURE__ */ jsx9(Text5, { weight: "bold", colorToken: "foreground-warning", children: message })
    ] }),
    /* @__PURE__ */ jsxs8(Text5, { colorToken: "foreground-secondary", "data-chromatic": "ignore", children: [
      "Going back in ",
      countdown,
      "s..."
    ] })
  ] }) });
};

// src/components/Uploader/state.ts
var initialState = {
  status: "idle"
};
function reducer(state, action) {
  switch (action.type) {
    case "DRAG_OVER":
      return { status: "drop" };
    case "DRAG_LEAVE":
      return { status: "idle" };
    case "START_UPLOAD":
      return { status: "uploading" };
    case "UPLOAD_FINISHED":
      return { status: "showing_image", image: action.image };
    case "INVALIDATE_IMAGE":
      return { status: "warning", warningMessage: action.warningMessage };
    case "UPLOAD_ERROR":
      return { status: "error", errorMessage: action.errorMessage };
    case "CLEAN_FILE":
      return { status: "idle" };
    case "RESET_STATUS":
      return { status: "idle" };
    case "IMAGE_SELECTED":
      return { status: "showing_image", image: action.image };
    default:
      return state;
  }
}

// src/components/Uploader/file-validator.ts
var validateFileDimension = async (file, maxDimension) => {
  if (!maxDimension) return true;
  try {
    const bitmap = await createImageBitmap(file);
    const isValid = validateDimension(bitmap, maxDimension);
    bitmap.close();
    return isValid;
  } catch (error) {
    console.error("Failed to create image bitmap:", error);
    return false;
  }
};

// src/components/Uploader/Uploader.tsx
import { Fragment as Fragment3, jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var Uploader = ({
  title = "Add an image",
  description = "JPEG, PNG and SVG - 1MB Max",
  accept = "image/*",
  maxSize = 1,
  maxDimension,
  selectedImage,
  disableMenu,
  container,
  zIndex,
  renderPreview,
  onUpload,
  onClean,
  ...htmlAttrs
}) => {
  const { setIsGalleryOpen } = useImagery();
  const inputId = useMemo(() => `imagery-uploader-${crypto.randomUUID()}`, []);
  const inputRef = useRef2(null);
  const [state, dispatch] = useReducer(
    reducer,
    selectedImage ? { status: "showing_image", image: selectedImage } : initialState
  );
  useEffect3(() => {
    if (selectedImage) {
      dispatch({
        type: "IMAGE_SELECTED",
        image: selectedImage
      });
    } else {
      dispatch({ type: "CLEAN_FILE" });
    }
  }, [selectedImage]);
  const [openDropdownMenu, setDropdownOpen] = useState2(false);
  const validateImage2 = async (file) => {
    if (!validateFormat(file, accept)) {
      dispatch({
        type: "INVALIDATE_IMAGE",
        warningMessage: `${file.type} is not supported.`
      });
      return false;
    }
    if (!validateSize(file, maxSize)) {
      dispatch({
        type: "INVALIDATE_IMAGE",
        warningMessage: `File exceeds the ${maxSize}MB limit.`
      });
      return false;
    }
    if (!await validateFileDimension(file, maxDimension)) {
      dispatch({
        type: "INVALIDATE_IMAGE",
        warningMessage: `Dimensions must not exceed ${maxDimension}px`
      });
      return false;
    }
    return true;
  };
  const handleOnUpload = async (image) => {
    const isImageValid = await validateImage2(image);
    if (!isImageValid) return;
    try {
      dispatch({ type: "START_UPLOAD" });
      const result = await onUpload(image);
      if (!result) {
        throw new Error("Upload failed");
      }
      dispatch({
        type: "UPLOAD_FINISHED",
        image: result?.url
      });
    } catch {
      dispatch({
        type: "UPLOAD_ERROR",
        errorMessage: "Something went wrong..."
      });
    }
  };
  const handleOnDrop = async (event) => {
    event.preventDefault();
    if (event.dataTransfer.files.length === 0) return;
    await handleOnUpload(event.dataTransfer.files[0]);
  };
  const handleDragOver = (event) => {
    event.preventDefault();
    dispatch({
      type: "DRAG_OVER"
    });
  };
  const handleDragLeave = (event) => {
    event.preventDefault();
    dispatch({
      type: "DRAG_LEAVE"
    });
  };
  const handleOnChange = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    await handleOnUpload(event.target.files[0]);
  };
  const handleOnUploadSelect = () => {
    inputRef.current?.click();
  };
  const handleClean = () => {
    dispatch({
      type: "CLEAN_FILE"
    });
    onClean?.();
  };
  const handleBack = () => {
    dispatch({
      type: "RESET_STATUS"
    });
  };
  if (state.status === "showing_image") {
    if (renderPreview) {
      return /* @__PURE__ */ jsx10(Fragment3, { children: renderPreview({ image: state.image, onClean: handleClean }) });
    }
    return /* @__PURE__ */ jsx10(UploaderBox, { status: state.status, as: "div", children: /* @__PURE__ */ jsx10(SelectedImage, { image: state.image, handleClean }) });
  }
  if (state.status === "error") {
    return /* @__PURE__ */ jsx10(ErrorMessage, { message: state.errorMessage, onTryAgain: handleBack });
  }
  if (state.status === "warning") {
    return /* @__PURE__ */ jsx10(WarningMessage, { message: state.warningMessage, onReset: handleBack });
  }
  if (state.status === "uploading") {
    return /* @__PURE__ */ jsx10(UploaderBox, { status: state.status, as: "div", children: /* @__PURE__ */ jsxs9(Stack6, { spacing: "small", xAlign: "center", children: [
      /* @__PURE__ */ jsx10(LoadingIcon, {}),
      /* @__PURE__ */ jsx10(Text6, { weight: "bold", colorToken: "foreground-primary", children: "Uploading file..." })
    ] }) });
  }
  return /* @__PURE__ */ jsxs9(
    DropdownMenu.Root,
    {
      open: !disableMenu && openDropdownMenu,
      onOpenChange: setDropdownOpen,
      children: [
        /* @__PURE__ */ jsx10(DropdownMenu.Trigger, { asChild: true, children: /* @__PURE__ */ jsxs9(
          UploaderBox,
          {
            ...htmlAttrs,
            status: state.status,
            onDrop: handleOnDrop,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onClick: disableMenu ? handleOnUploadSelect : void 0,
            type: "button",
            children: [
              /* @__PURE__ */ jsx10(
                "input",
                {
                  ref: inputRef,
                  id: inputId,
                  onChange: handleOnChange,
                  accept,
                  type: "file",
                  "aria-hidden": true
                }
              ),
              state.status === "drop" ? /* @__PURE__ */ jsx10(DropContent, { spacing: "small", xAlign: "center", children: /* @__PURE__ */ jsxs9(Row5, { spacing: "small", yAlign: "center", children: [
                /* @__PURE__ */ jsx10(
                  Icon7,
                  {
                    icon: faUpload,
                    colorToken: "foreground-primary"
                  }
                ),
                /* @__PURE__ */ jsx10(Text6, { weight: "bold", colorToken: "foreground-primary", children: "Drop file here" })
              ] }) }) : /* @__PURE__ */ jsxs9(Stack6, { spacing: "small", xAlign: "center", children: [
                /* @__PURE__ */ jsxs9(Row5, { spacing: "small", yAlign: "center", children: [
                  /* @__PURE__ */ jsx10(
                    Icon7,
                    {
                      icon: faCirclePlus,
                      colorToken: "foreground-primary"
                    }
                  ),
                  /* @__PURE__ */ jsx10(Text6, { weight: "bold", colorToken: "foreground-primary", children: title })
                ] }),
                /* @__PURE__ */ jsx10(Text6, { colorToken: "foreground-input-placeholder", children: description })
              ] })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx10(DropdownMenu.Portal, { container, children: /* @__PURE__ */ jsx10(DropdownMenu.Content, { side: "bottom", asChild: true, children: /* @__PURE__ */ jsxs9(StyledDropdownContent, { $zIndex: zIndex, children: [
          /* @__PURE__ */ jsx10(DropdownMenu.Item, { onClick: handleOnUploadSelect, children: /* @__PURE__ */ jsxs9(Row5, { spacing: "small", yAlign: "center", children: [
            /* @__PURE__ */ jsx10(
              Icon7,
              {
                icon: faUpload,
                colorToken: "foreground-primary"
              }
            ),
            " ",
            "Upload image"
          ] }) }),
          /* @__PURE__ */ jsx10(DropdownMenu.Item, { onClick: () => setIsGalleryOpen(true), children: /* @__PURE__ */ jsxs9(Row5, { spacing: "small", yAlign: "center", children: [
            /* @__PURE__ */ jsx10(
              Icon7,
              {
                icon: faImages,
                colorToken: "foreground-primary"
              }
            ),
            "Select from gallery"
          ] }) })
        ] }) }) })
      ]
    }
  );
};

// src/components/Provider.tsx
import { useState as useState3, useMemo as useMemo2, useCallback as useCallback2 } from "react";
import { jsx as jsx11 } from "react/jsx-runtime";
var convertFileToBase64 = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", function handleFileLoad(event) {
      const fileData = event.target?.result;
      resolve({
        fileName: file.name,
        fileType: file.type,
        fileData
      });
    });
    reader.readAsDataURL(file);
  });
};
var handleApiCall = async (apiCall) => {
  try {
    const result = await apiCall;
    return {
      ok: true,
      json: () => Promise.resolve(result)
    };
  } catch (error) {
    return {
      ok: false,
      json: () => Promise.reject(error)
    };
  }
};
var Provider = ({
  children,
  fetchImages: fetchImagesApi,
  uploadImage: uploadImageApi
}) => {
  const [images, setImages] = useState3([]);
  const [isLoading, setIsLoading] = useState3(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState3(false);
  const [pageInfo, setPageInfo] = useState3({ number: 1, size: 50, total: 0 });
  const { size } = pageInfo;
  const fetchImages = useCallback2(
    async (number = 1) => {
      try {
        setIsLoading(true);
        const response = await handleApiCall(
          fetchImagesApi({
            number,
            size
          })
        );
        if (!response.ok) throw new Error("Failed to fetch images");
        const { data, pageInfo: pageInfoResponse } = await response.json();
        const { number: numberResponse, total: totalResponse } = pageInfoResponse;
        setImages((prev) => number === 1 ? data : [...prev, ...data]);
        setPageInfo({
          number: numberResponse,
          size,
          total: totalResponse
        });
      } catch (error) {
        console.error("Error fetching images:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [size, fetchImagesApi]
  );
  const uploadImage = useCallback2(
    async (originalFile) => {
      try {
        const fileData = await convertFileToBase64(originalFile);
        const response = await handleApiCall(uploadImageApi(fileData));
        if (!response.ok) {
          throw new Error("Upload failed");
        }
        const result = await response.json();
        return result;
      } catch (error) {
        console.error("Image upload failed:", error);
        throw error;
      }
    },
    [uploadImageApi]
  );
  const value = useMemo2(
    () => ({
      images,
      setImages,
      isLoading,
      setIsLoading,
      isGalleryOpen,
      setIsGalleryOpen,
      pageInfo,
      setPageInfo,
      fetchImages,
      uploadImage
    }),
    [images, isLoading, isGalleryOpen, pageInfo, uploadImage, fetchImages]
  );
  return /* @__PURE__ */ jsx11(Context_default.Provider, { value, children });
};

// src/utils/file-utils.ts
var formatBytes = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round(bytes / k ** i)} ${sizes[i]}`;
};
var fetchImageSize = async (url) => {
  try {
    const response = await fetch(url, { method: "HEAD" });
    const contentLength = response.headers.get("content-length");
    return contentLength ? Number.parseInt(contentLength, 10) : null;
  } catch {
    return null;
  }
};
var getFileName = (url) => {
  try {
    const urlObj = new URL(url);
    const { pathname } = urlObj;
    const parts = pathname.split("/");
    return parts[parts.length - 1] || "image";
  } catch {
    return "image";
  }
};
export {
  Context_default as Context,
  Gallery as ImageryGallery,
  Provider as ImageryProvider,
  Uploader as ImageryUploader,
  fetchImageSize,
  formatBytes,
  getFileName,
  useImagery,
  validateFormat,
  validateSize
};
//# sourceMappingURL=index.mjs.map